import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface LoadingState {
    isLoading: boolean;
    message?: string;
    progress?: number;
}

@Injectable({
    providedIn: 'root'
})
export class LoadingService {
    private fullScreenLoadingSubject = new BehaviorSubject<LoadingState>({ isLoading: false });
    private tableLoadingMap = new Map<string, BehaviorSubject<boolean>>();

    public fullScreenLoading$: Observable<LoadingState> = this.fullScreenLoadingSubject.asObservable();

    constructor() {}

    /**
     * Exibe loader em tela cheia
     * @param message Mensagem opcional para exibir
     * @param progress Progresso opcional (0-100)
     */
    showFullScreenLoader(message?: string, progress?: number): void {
        this.fullScreenLoadingSubject.next({
            isLoading: true,
            message,
            progress
        });
    }

    /**
     * Esconde loader em tela cheia
     */
    hideFullScreenLoader(): void {
        this.fullScreenLoadingSubject.next({ isLoading: false });
    }

    /**
     * Atualiza mensagem ou progresso do loader atual
     */
    updateFullScreenLoader(message?: string, progress?: number): void {
        const currentState = this.fullScreenLoadingSubject.value;
        this.fullScreenLoadingSubject.next({
            ...currentState,
            message: message ?? currentState.message,
            progress: progress ?? currentState.progress
        });
    }

    /**
     * Define estado de loading para uma tabela específica
     * @param tableId Identificador único da tabela
     * @param isLoading Estado de loading
     */
    setTableLoading(tableId: string, isLoading: boolean): void {
        if (!this.tableLoadingMap.has(tableId)) {
            this.tableLoadingMap.set(tableId, new BehaviorSubject<boolean>(false));
        }
        this.tableLoadingMap.get(tableId)!.next(isLoading);
    }

    /**
     * Obtém observable de loading para uma tabela específica
     * @param tableId Identificador único da tabela
     */
    getTableLoading$(tableId: string): Observable<boolean> {
        if (!this.tableLoadingMap.has(tableId)) {
            this.tableLoadingMap.set(tableId, new BehaviorSubject<boolean>(false));
        }
        return this.tableLoadingMap.get(tableId)!.asObservable();
    }

    /**
     * Executa uma operação com loading automático
     * @param operation Promise ou Observable para executar
     * @param message Mensagem opcional durante o loading
     */
    async withLoading<T>(operation: Promise<T>, message?: string): Promise<T> {
        try {
            this.showFullScreenLoader(message);
            const result = await operation;
            return result;
        } finally {
            this.hideFullScreenLoader();
        }
    }
}
